/**
 * Workspace Knowledge — the comb, kept (documentation/ai-map/features/workspace-knowledge §5).
 *
 * One source of truth (Deposits) and five derivations (Structure, Meaning, Recall, Distillation,
 * Reach). Every number here is DERIVED NOW by the server — nothing in this contract stores an
 * "indexed: yes". The desk reads `knowledge.status` / `knowledge.activity` (any member) and acts
 * through `knowledge.refresh` / `knowledge.refresh.cancel` (`workspaceUpdate` only).
 *
 * A refresh answers with a TYPED result, never a thrown string: 202 started, 409 already running
 * (with who started the live one), 503 refused with the server's own sentence (graph full, graph
 * unreachable, engine switched off), 403 not yours to act on. Only a genuine failure (500, network)
 * throws.
 */
import { z } from "zod";

export const KNOWLEDGE_STAGES = [
  "deposits",
  "structure",
  "meaning",
  "recall",
  "distillation",
  "reach",
] as const;
export type KnowledgeStageId = (typeof KNOWLEDGE_STAGES)[number];

export const KNOWLEDGE_STATUSES = ["sound", "behind", "broken", "off"] as const;
export type KnowledgeStatusWord = (typeof KNOWLEDGE_STATUSES)[number];

/** What `knowledge.refresh` accepts: the four stages that can be rebuilt, or all of them. */
export const KNOWLEDGE_REFRESH_TARGETS = [
  "structure",
  "meaning",
  "recall",
  "distillation",
  "everything",
] as const;
export type KnowledgeRefreshTarget = (typeof KNOWLEDGE_REFRESH_TARGETS)[number];

const nullableString = z.string().nullable().default(null);

/** An unknown status word from a newer server reads as `broken` — never as a confident `sound`. */
const StatusWordSchema = z
  .string()
  .transform(
    (value): KnowledgeStatusWord =>
      (KNOWLEDGE_STATUSES as readonly string[]).includes(value)
        ? (value as KnowledgeStatusWord)
        : "broken",
  );

export const KnowledgeLastRefreshSchema = z.object({
  runId: z.string(),
  at: z.string(),
  by: nullableString,
  outcome: nullableString,
});
export type KnowledgeLastRefresh = z.infer<typeof KnowledgeLastRefreshSchema>;

export const KnowledgeActionSchema = z.object({
  verb: z.string(),
  enabled: z.boolean(),
  disabledReason: nullableString,
});
export type KnowledgeAction = z.infer<typeof KnowledgeActionSchema>;

/**
 * `counts` is open in §5 (`counts:{...}`); the server's records (WorkspaceKnowledgeRules.cs) are,
 * per stage:
 *   deposits      documents · collections · comments · repositories · files · groveFlows
 *   structure     documents · documentNodes · missing · missingTitles[] · files · fileNodes ·
 *                 collections · collectionNodes
 *   meaning       read · of · tooShort
 *   recall        documents{embedded, of} · files{embedded, of}
 *   distillation  declared · scopes · nodes · fresh · stale · exhausted
 *   reach         documents · files · components · summaries · concepts
 * Kept open here (the desk reads them through `knowledgeView.ts` and drops a line it cannot
 * read) so a new count never breaks the page.
 */
export const KnowledgeStageSchema = z.object({
  id: z.enum(KNOWLEDGE_STAGES),
  status: StatusWordSchema,
  sentence: z.string().default(""),
  counts: z.record(z.string(), z.unknown()).default({}),
  reasons: z.array(z.string()).default([]),
  lastRefresh: KnowledgeLastRefreshSchema.nullable().default(null),
  action: KnowledgeActionSchema.nullable().default(null),
});
export type KnowledgeStage = z.infer<typeof KnowledgeStageSchema>;

/** The one live refresh of this workspace (§3.3: one at a time). */
export const KnowledgeRunningSchema = z.object({
  runId: z.string(),
  stage: z.string(),
  startedBy: nullableString,
  startedAt: nullableString,
  done: z.number().default(0),
  total: z.number().default(0),
  /** What the job is doing now ("documents", "code files", "reading documents" …). */
  phase: nullableString,
});
export type KnowledgeRunning = z.infer<typeof KnowledgeRunningSchema>;

export const KnowledgeStatusSchema = z.object({
  workspace: z.object({ id: z.string(), name: z.string() }),
  generatedAt: nullableString,
  graph: z
    .object({ available: z.boolean(), error: nullableString })
    .default({ available: true, error: null }),
  /**
   * Additive to §5 — the server's own answer to "may this caller act here" (it enforces the gate,
   * so where present it wins over the desk's ability map).
   */
  access: z
    .object({ read: z.boolean(), act: z.boolean(), reason: nullableString })
    .nullable()
    .default(null),
  stages: z.array(KnowledgeStageSchema).default([]),
  running: KnowledgeRunningSchema.nullable().default(null),
});
export type KnowledgeStatus = z.infer<typeof KnowledgeStatusSchema>;

export const KNOWLEDGE_RUN_STATES = [
  "queued",
  "running",
  "stopping",
  "done",
  "failed",
  "cancelled",
] as const;
export type KnowledgeRunState = (typeof KNOWLEDGE_RUN_STATES)[number];

/** The Runs table speaks `Completed`/`Canceled`/`Pending`; the desk speaks six words. */
function runState(value: unknown): KnowledgeRunState {
  const word = typeof value === "string" ? value.toLowerCase() : "";
  switch (word) {
    case "queued":
    case "pending":
    case "scheduled":
      return "queued";
    case "running":
    case "started":
    case "inprogress":
    case "in_progress":
      return "running";
    case "stopping":
    case "cancelling":
    case "canceling":
      return "stopping";
    case "done":
    case "completed":
    case "succeeded":
    case "success":
      return "done";
    case "cancelled":
    case "canceled":
      return "cancelled";
    default:
      return "failed";
  }
}

/** One knowledge refresh, as `knowledge.activity` lists it (newest first). */
export const KnowledgeRunSchema = z.preprocess(
  (raw) => {
    const value = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
    return { ...value, runId: value.runId ?? value.id, status: runState(value.status) };
  },
  z.object({
    runId: z.string(),
    stage: z.string(),
    status: z.enum(KNOWLEDGE_RUN_STATES),
    startedBy: nullableString,
    startedAt: nullableString,
    finishedAt: nullableString,
    outcome: nullableString,
    done: z.number().nullable().default(null),
    total: z.number().nullable().default(null),
    phase: nullableString,
    force: z.boolean().default(false),
  }),
);
export type KnowledgeRun = z.infer<typeof KnowledgeRunSchema>;

/**
 * The size a refresh announced when it was accepted. Keys vary by stage — structure
 * `{documents, collections, files, comments, minutes}`, meaning `{toRead, minutes}`, recall
 * `{toEmbed, minutes}`, distillation `{parts, minutes}`; `minutes` is null until measured.
 */
export type KnowledgeEstimate = Record<string, number | null>;

export type KnowledgeRefusalCode =
  | "graph_capacity_exceeded"
  | "graph_unreachable"
  | "stage_off"
  | (string & {});

export type KnowledgeRefreshResult =
  | { kind: "started"; runId: string; stage: string; estimate: KnowledgeEstimate }
  /** 409 — one live refresh per workspace; `running` names who started it and when. */
  | { kind: "already_running"; running: KnowledgeRunning | null; sentence: string | null }
  /** 503 — the server refused before starting, and says why in a sentence. */
  | { kind: "refused"; error: KnowledgeRefusalCode; sentence: string }
  /** 403 — the caller may see this workspace's knowledge but not act on it. */
  | { kind: "forbidden"; sentence: string | null };

export interface KnowledgeApi {
  status(): Promise<KnowledgeStatus>;
  refresh(stage: KnowledgeRefreshTarget, force?: boolean): Promise<KnowledgeRefreshResult>;
  /** `cancelled` (closed now), `stopping` (the job stops at its next batch), or the final word. */
  cancel(runId: string): Promise<KnowledgeRunState>;
  activity(limit?: number): Promise<KnowledgeRun[]>;
}

/** The run-state word of a cancel answer or an activity row, normalised. */
export function toRunState(value: unknown): KnowledgeRunState {
  return runState(value);
}

export function isLiveRun(run: Pick<KnowledgeRun, "status">): boolean {
  return run.status === "running" || run.status === "queued" || run.status === "stopping";
}
